import React, { useEffect, useState } from "react";
import * as S from "./PageWrapper.style";
import { UpgradeButton } from "./UpgradeButton";
import { TryForFreeBanner } from "./TryForFreeBanner";
import { usePlanFeature } from "~/hooks/usePlanFeature";
import { Limits } from "~/types/billing";
import { Notifications } from "~/components/notifications";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { PlansModal } from "~/components/plans/plansModal";
import { ResumeModal } from "~/components/resumeModal";
import { useLocation, useNavigate } from "react-router-dom";
import { useAccount } from "~/hooks/useAccount";
import { useClarityIdentify } from "~/hooks/useClarityIdentify";
import { LimitsModal } from "../limits-modal";
import { OnboardingPage } from "../onboarding-flow";
import {
  PICKED_CATEGORIES_KEY,
  readPickedCategories,
  type OnboardingCategory,
} from "../onboarding-flow/onboarding-utils";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { usePlans } from "~/api/billing/queries";
import { paths } from "~/router/paths";
import { SubscriptionType } from "~/types/billing";
import { PlanStatus } from "~/types/account";
import { ModalConnectToStore } from "../connect-to-store/modal";
import { PlanUpgradeModal } from "../plans/PlanUpgradeModal";
import { ShowUpgradeModal } from "../upgrade-funnel/showUpgradeModal";
import { DTPromotionModal } from "../dt-promotion-modal";

interface IProps {
  children: React.ReactNode;
}

export const SKIP_NEXT_ONBOARDING_INJECT_KEY = "skip_next_onboarding_inject";

export const PageWrapper: React.FC<IProps> = ({ children = null }) => {
  useClarityIdentify();
  const { isFeatureDisabled } = usePlanFeature();
  const location = useLocation();
  const { getAccount, account } = useAccount();
  useMediaQuery("desktop");
  const queryParams = new URLSearchParams(location.search);
  const isSettingsPage = location.pathname.includes("settings");
  const isNotMobile = useMediaQuery("tablet");
  const [isShowingOnboarding, setIsShowingOnboarding] = useState(false);
  const planId = queryParams.get("plan_id");
  const showRating = queryParams.get("show_rating");
  const onboarding = queryParams.get("onboarding");
  const { data: plans, refetch: refetchPlans } = usePlans();
  const activePlans = plans?.filter(
    (plan) => plan.status === PlanStatus.ACTIVE
  );
  const resolveCheckoutPlanId = (
    plansList: typeof activePlans
  ): string | undefined =>
    plansList?.find(
      (plan) =>
        plan.name === "Empire" &&
        plan.interval === SubscriptionType.MONTHLY &&
        plan.for_winning
    )?.id ?? plansList?.[0]?.id;
  const navigate = useNavigate();

  // Paid after the onboarding: the welcome screen with the picked tools.
  const [welcomeCategories, setWelcomeCategories] = useState<
    OnboardingCategory[] | null
  >(null);

  useEffect(() => {
    // Checkout returns with ?plan_id= once the payment went through.
    const isPaid = Boolean(account?.active_subscription) || Boolean(planId);
    const picked = readPickedCategories();
    if (isPaid && picked) setWelcomeCategories(picked);
  }, [account, planId]);

  const finishWelcome = () => {
    localStorage.removeItem(PICKED_CATEGORIES_KEY);
    setWelcomeCategories(null);
  };

  // "Unlock Smartli" or "Skip": the existing checkout page.
  const openCheckout = async (categories: OnboardingCategory[]) => {
    localStorage.setItem(PICKED_CATEGORIES_KEY, JSON.stringify(categories));

    if (DISABLE_PAYMENTS) {
      setIsShowingOnboarding(false);
      setWelcomeCategories(categories);
      return;
    }

    let selectedPlanId = resolveCheckoutPlanId(activePlans);

    if (!selectedPlanId) {
      const { data: freshPlans } = await refetchPlans();
      const freshActivePlans = freshPlans?.filter(
        (plan) => plan.status === PlanStatus.ACTIVE
      );
      selectedPlanId = resolveCheckoutPlanId(freshActivePlans);
    }

    if (!selectedPlanId) {
      sessionStorage.setItem(SKIP_NEXT_ONBOARDING_INJECT_KEY, "true");
      setIsShowingOnboarding(false);
      navigate(paths.settings.plans);
      return;
    }

    setIsShowingOnboarding(false);
    navigate(paths.checkout.index, { state: { selectedPlanId } });
  };

  useEffect(() => {
    const checkOnboardingCondition = async () => {
      const account = await getAccount();

      if (onboarding && account && !account?.active_subscription) {
        setIsShowingOnboarding(true);
      }

      const searchParams = new URLSearchParams(window.location.search);

      const skipThisInjection =
        sessionStorage.getItem(SKIP_NEXT_ONBOARDING_INJECT_KEY) === "true";
      if (skipThisInjection) {
        sessionStorage.removeItem(SKIP_NEXT_ONBOARDING_INJECT_KEY);
      }

      if (
        account &&
        !account?.active_subscription &&
        !account?.has_subscribed_before &&
        !planId &&
        !showRating &&
        !skipThisInjection
      ) {
        searchParams.set("onboarding", "true");
        searchParams.set("origin", account?.signup_origin);
        navigate(`?${searchParams.toString()}`);
      } else if (!!onboarding) {
        setIsShowingOnboarding(false);
        searchParams.delete("onboarding");
        const queryParams = searchParams.toString();
        const newUrl =
          window.location.pathname + (!!queryParams ? `?${queryParams}` : "");
        window.history.replaceState(null, "", newUrl);
      }
    };

    checkOnboardingCondition();
  }, [planId, onboarding]);

  // A new account gets the onboarding straight away, instead of seeing the
  // dashboard until the ?onboarding= redirect above has run.
  const isNewAccount =
    !DISABLE_PAYMENTS &&
    // Where openCheckout sends the user when no plan can be resolved.
    location.pathname !== paths.settings.plans &&
    Boolean(account) &&
    !account?.active_subscription &&
    !account?.has_subscribed_before &&
    !planId &&
    !showRating;

  if (isShowingOnboarding || welcomeCategories || isNewAccount) {
    return (
      <OnboardingPage
        welcomeCategories={welcomeCategories}
        plans={activePlans}
        onCheckout={openCheckout}
        onFinish={finishWelcome}
      />
    );
  }

  return (
    <S.PageWrapper
      padding={isSettingsPage ? "24px 25px 58px" : "24px 24px 58px"}>
      <S.PageHeader>
        {isFeatureDisabled(Limits.PAID_PLAN) ? <UpgradeButton /> : null}
        {isNotMobile ? <Notifications /> : null}
      </S.PageHeader>
      <S.PageMaxWidth>
        <TryForFreeBanner />
        <>{children}</>
      </S.PageMaxWidth>
      <PlansModal />
      <ResumeModal />
      <LimitsModal />
      <ModalConnectToStore />
      <ShowUpgradeModal />
      <PlanUpgradeModal />
      <DTPromotionModal />
    </S.PageWrapper>
  );
};

export const PageWrapperFullScreen: React.FC<IProps> = ({
  children = null,
}) => {
  useClarityIdentify();

  return (
    <S.PageWrapperFullscreen>
      <PlansModal />
      <ResumeModal />
      <LimitsModal />
      <>{children}</>
    </S.PageWrapperFullscreen>
  );
};
